import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { htmlToText } from 'html-to-text';

import PATHS from '../../../../Routers/PATHS';
import { curriculumArtwork } from '../../assets';
import { Button } from '../../../shared/ui';

/**
 * One curriculum in the catalogue grid.
 *
 * Figma node 16383:22306, a 564×562 card:
 *   frame     white, 24px radius, 1px #e2e4e8, 0 12px 24px rgba(0,0,0,0.01)
 *   artwork   564×274 on #e6eef8, the cover centred at 330 wide
 *   body      24px of padding, an 8px column
 *   badge     11px bold #2563eb on #edf2ff, 6px radius, 10×4 padding
 *   title     18px bold #181a1e
 *   excerpt   14px #606670 at 1.5 leading, three lines
 *   buttons   two, 162 wide, 13px bold, 12px radius — subscribe on the
 *             brand blue, details on the dark fill
 *
 * The design draws the same certificate nine times, so it says nothing about
 * what happens when a name runs long or a description runs short. Both are
 * clamped — two lines for the name, three for the excerpt — and the buttons
 * are pushed to the floor of the card, so a row of cards lines up whatever the
 * backend returns.
 *
 * `description` arrives as HTML written in the admin panel. It is flattened to
 * text rather than parsed: this is a three-line excerpt, and the markup around
 * it would only bring the admin's own headings and spacing into a card that
 * has none.
 *
 * The artwork is the certificate's own `coverImgUrl`, set in the admin panel.
 * A certificate without one — or whose file no longer loads — falls back to the
 * design's mockup, so a gap in the data never leaves an empty panel.
 *
 * @param {{id, name, code, description, coverImgUrl}} certificate
 */
const CertificateCard = ({ certificate }) => {
    const { t } = useTranslation();
    const { id, name, code, description, coverImgUrl } = certificate;

    // Set when the backend's file fails to load. Cleared when the URL changes,
    // so a corrected image in the admin panel is tried again.
    const [coverFailed, setCoverFailed] = useState(false);
    useEffect(() => setCoverFailed(false), [coverImgUrl]);

    const artwork = coverImgUrl && !coverFailed ? coverImgUrl : curriculumArtwork;

    /*
     * A description can be a whole page of HTML, and only three lines of it are
     * ever seen. Flattening the first 1200 characters keeps the work — and the
     * text sitting in the DOM behind the clamp — proportionate to that.
     */
    const excerpt = useMemo(
        () => (description ? htmlToText(String(description).slice(0, 1200), { wordwrap: null }).trim() : ''),
        [description]
    );

    const detailsUrl = `${PATHS.LANDING_CERTIFICATIONS.URL}/${id}`;

    /*
     * 562px is the design's card height, and a floor rather than a fixed size.
     * The design draws one certificate nine times, so every card there has a
     * one-line name and a description that fills exactly three lines. Real ones
     * do not, and a card whose text runs longer should grow rather than spill.
     * Under the floor the slack collects above the buttons, which `mt-auto`
     * holds down — which is the air the design has there.
     */
    return (
        <li className="tw-flex tw-h-full tw-flex-col tw-overflow-hidden tw-rounded-[24px] tw-border tw-border-v2-ground-500 tw-bg-white tw-shadow-[0_12px_24px_rgba(0,0,0,0.01)] lg:tw-min-h-[562px]">
            {/* A fixed 564:274 rather than a fixed height, so the artwork panel
                keeps its proportion as the column narrows. `object-cover` fills
                the panel whatever shape the uploaded file is, trimming the
                edges of one that is not 564:274 — so artwork for this slot
                should be cut to that ratio. The `bg` underneath is the design's
                tint, and shows only while the image loads. */}
            <div className="tw-aspect-[564/274] tw-w-full tw-bg-v2-brand-wash">
                <img
                    src={artwork}
                    alt=""
                    aria-hidden
                    loading="lazy"
                    onError={() => setCoverFailed(true)}
                    className="tw-h-full tw-w-full tw-object-cover"
                />
            </div>

            <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-[8px] tw-p-[24px] tw-text-start">
                {code && (
                    <span className="tw-inline-flex tw-w-fit tw-rounded-[6px] tw-bg-v2-brand-tint tw-px-[10px] tw-py-[4px] tw-text-[11px] tw-font-bold tw-text-v2-brand">
                        {code}
                    </span>
                )}

                <h3 className="tw-line-clamp-2 tw-text-v2-lg tw-font-bold tw-leading-[1.4] tw-text-v2-text-heading">
                    {name}
                </h3>

                {excerpt && (
                    <p className="tw-line-clamp-3 tw-text-v2-sm tw-leading-[1.5] tw-text-v2-text-excerpt">
                        {excerpt}
                    </p>
                )}

                {/* `mt-auto` is what pins the pair to the floor of the card, so
                    they line up across a row whose names and excerpts do not.
                    Subscribe comes first, which puts it at the start edge —
                    the right in Arabic, where the design has it. */}
                <div className="tw-mt-auto tw-flex tw-gap-[12px] tw-pt-[16px]">
                    <Button
                        to={`${detailsUrl}#packages`}
                        variant="primary"
                        size="compact"
                        className="tw-flex-1 tw-max-w-[162px]"
                    >
                        {t('v2.catalogue.card.subscribe')}
                    </Button>
                    <Button
                        to={detailsUrl}
                        variant="muted"
                        size="compact"
                        className="tw-flex-1 tw-max-w-[162px]"
                    >
                        {t('v2.catalogue.card.details')}
                    </Button>
                </div>
            </div>
        </li>
    );
};

export default CertificateCard;
